import React, { useState } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  ProfileSection,
  UsageSection,
  BillingSection,
  ThresholdSettings,
  CreditHistory,
  CreditPackages,
} from "../components/settings";
import { useSettingsData } from "../hooks/useSettingsData";
import { isAdminUser } from "../services/auth";
import { RETRY } from "../config/constants";
import { ERROR_CATEGORY_HEADINGS } from "../hooks/useSettingsRetry";
import "./Settings.css";

const TABS = [
  { id: "account", label: "Account" },
  { id: "usage", label: "Usage & Credits" },
  { id: "billing", label: "Billing" },
];

const Settings = () => {
  const [activeTab, setActiveTab] = useState("account");

  const {
    // Data
    user,
    usageSummary,
    invoiceHistory,
    plans,
    planOrder,
    creditPackages,
    salesEmail,
    credits,
    thresholds,
    usageThisMonth,
    monthlyAllocations,
    currentPlanId,
    planStatus,
    planExpiresAt,

    // Loading states
    loadingUser,
    loadingUsage,
    loadingInvoices,
    loadingPackages,
    creditsLoading,
    isRetrying,

    // Error states
    loadError,
    errorCategory,
    retryCount,
    debounceRemaining,

    // Billing action states
    upgradingPlan,
    openingPortal,
    confirmingUpgrade,
    purchasingPackage,

    // Handlers
    handleUpgradeClick,
    handleUpgradeConfirm,
    handleUpgradeCancel,
    handleManageSubscription,
    handleBuyCredits,
    handleRetry,
    refreshCredits,
    toast,
  } = useSettingsData();

  // Show error state if user (critical data) failed to load
  const userLoadComplete = !loadingUser;
  const userLoadFailed = userLoadComplete && !user && loadError;

  // Show loading spinner during initial load or during retries
  const showLoadingSpinner =
    (loadingUser && loadingUsage && loadingInvoices) || isRetrying;

  if (showLoadingSpinner && !userLoadFailed) {
    const loadingText = isRetrying ? "Retrying..." : "Loading settings...";
    return (
      <div className="settings-page">
        <LoadingSpinner centered text={loadingText} />
      </div>
    );
  }

  if (userLoadFailed) {
    const retriesExhausted = retryCount >= RETRY.MAX_ATTEMPTS;
    const isDebouncing = debounceRemaining > 0;
    const buttonDisabled = retriesExhausted || isRetrying || isDebouncing;

    let buttonText = "Try Again";
    if (isRetrying) {
      buttonText = "Retrying...";
    } else if (isDebouncing) {
      buttonText = `Wait ${debounceRemaining}s...`;
    } else if (retriesExhausted) {
      buttonText = "Retries exhausted";
    }

    const heading =
      ERROR_CATEGORY_HEADINGS[errorCategory] ?? "Unable to Load Settings";

    return (
      <div className="settings-page">
        <div className="settings-error-state">
          <svg
            className="error-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2>{heading}</h2>
          <p>{loadError}</p>
          <button
            className="retry-button"
            onClick={handleRetry}
            disabled={buttonDisabled}
          >
            {buttonText}
          </button>
          {retryCount > 0 && !retriesExhausted && (
            <p className="retry-count">
              Retry attempt {retryCount} of {RETRY.MAX_ATTEMPTS}
            </p>
          )}
          {retriesExhausted && (
            <p className="retry-count">Refresh the page to try again</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1>Settings</h1>
      <p className="settings-subtitle">Manage your account and view usage</p>

      <div className="settings-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`settings-tab-btn${activeTab === tab.id ? " settings-tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "account" && (
        <ProfileSection user={user} loading={loadingUser} />
      )}

      {activeTab === "usage" && (
        <>
          <UsageSection
            usageSummary={usageSummary}
            loading={loadingUsage}
            credits={credits}
            monthlyAllocations={monthlyAllocations}
            usageThisMonth={usageThisMonth}
            creditsLoading={creditsLoading}
            isAdmin={isAdminUser(user)}
          />

          <ThresholdSettings
            thresholds={thresholds}
            loading={creditsLoading}
            onSaved={refreshCredits}
            toast={toast}
          />

          <CreditPackages
            packages={creditPackages}
            loading={loadingPackages}
            purchasing={purchasingPackage}
            onBuy={handleBuyCredits}
          />

          <CreditHistory toast={toast} />
        </>
      )}

      {activeTab === "billing" && (
        <BillingSection
          plans={plans}
          planOrder={planOrder}
          currentPlanId={currentPlanId}
          planStatus={planStatus}
          planExpiresAt={planExpiresAt}
          invoiceHistory={invoiceHistory}
          loadingInvoices={loadingInvoices}
          upgradingPlan={upgradingPlan}
          openingPortal={openingPortal}
          confirmingUpgrade={confirmingUpgrade}
          salesEmail={salesEmail}
          toast={toast}
          onUpgradeClick={handleUpgradeClick}
          onUpgradeConfirm={handleUpgradeConfirm}
          onUpgradeCancel={handleUpgradeCancel}
          onManageSubscription={handleManageSubscription}
        />
      )}
    </div>
  );
};

export default Settings;
